import { useEffect } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { ApiError, api, request } from '../client';

const OFFICE_ROLES: readonly string[] = ['owner', 'dispatcher', 'accountant', 'mechanic'];
import { qk } from '../keys';
import { useSession } from '../session';
import type { LoginResult, Org, SessionUser } from '../types';

export interface LoginInput {
  email: string;
  password: string;
  totp?: string;
}

export function useLogin() {
  const setSession = useSession((s) => s.setSession);
  return useMutation({
    mutationFn: async (input: LoginInput) => {
      const result = await request<LoginResult>('/auth/login', { method: 'POST', body: input, anonymous: true });
      /**
       * Пултът е за офиса. Клиент или шофьор с верни данни иначе влиза, а всяка
       * страница отговаря „нямате права" — изглежда като счупена система.
       * Сесията му се затваря веднага и му се казва къде е неговото място.
       */
      if (!OFFICE_ROLES.includes(result.user.role)) {
        void request('/auth/logout', {
          method: 'POST',
          body: { refreshToken: result.refreshToken, allDevices: false },
          anonymous: true,
        }).catch(() => undefined);
        throw new ApiError(
          403,
          'WRONG_APP',
          result.user.role === 'customer'
            ? 'Този профил е за клиентския портал: kmdx.ovyra.ai/portal'
            : 'Този профил е за шофьорското приложение на телефона.',
        );
      }
      return result;
    },
    onSuccess: (result) => setSession(result),
    // Съобщението за грешен код се показва във формата, не като балон.
    meta: { silent: true },
  });
}

export function useLogout() {
  const clear = useSession((s) => s.clear);
  const refreshToken = useSession((s) => s.refreshToken);
  return useMutation({
    mutationFn: async () => {
      try {
        await api.post('/auth/logout', { refreshToken, allDevices: false });
      } catch {
        // Дори при отказ от сървъра сесията се чисти локално.
      }
    },
    onSettled: () => clear(),
    meta: { silent: true },
  });
}

/**
 * При отваряне на раздела има само refresh токен. Тази заявка вади access
 * токен и потвърждава кой е влезлият, преди рутерът да пусне екраните.
 */
export function useSessionBootstrap(): void {
  const refreshToken = useSession((s) => s.refreshToken);
  const setUser = useSession((s) => s.setUser);
  const finish = useSession((s) => s.finishBootstrap);

  const { data, isError, isSuccess } = useQuery({
    queryKey: qk.session,
    queryFn: () => api.get<SessionUser>('/auth/me'),
    enabled: refreshToken !== null,
    retry: false,
    staleTime: 5 * 60_000,
    meta: { silent: true },
  });

  useEffect(() => {
    if (isSuccess && data) {
      setUser(data);
      finish();
    }
    if (isError) finish();
  }, [isSuccess, isError, data, setUser, finish]);
}

export function useOrg() {
  return useQuery({
    queryKey: qk.org,
    queryFn: () => api.get<Org>('/org'),
    staleTime: 60 * 60_000,
    meta: { silent: true },
  });
}
